import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Avatar, Typography, CircularProgress } from '@mui/material';
import { PhotoCamera, Person } from '@mui/icons-material';
import { PhotoUploadSectionProps } from '../../types/profileForm.types';
import { setAlertData } from '../../../../Redux/Screen/Action';
import { useDispatch } from 'react-redux';

const PhotoUpload: React.FC<PhotoUploadSectionProps> = ({
  currentPhoto,
  onPhotoUpload,
  loading = false,
}) => {
  const [isUploaded, setIsUploaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentPhoto || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();

  useEffect(() => {
    setPreviewUrl(currentPhoto || null);
    setIsUploaded(!!currentPhoto);
  }, [currentPhoto]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPG, PNG, or GIF)');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      dispatch(setAlertData({
        alertMessage: `File size must be less than 5MB`,
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    try {
      await onPhotoUpload(file);
      setIsUploaded(true);
    } catch (error) {
      console.error('Upload failed:', error);
      setPreviewUrl(currentPhoto || null);
      alert('Upload failed. Please try again.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: 3,
        mb: 4,
        p: 3,
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #dee2e6',
      }}
    >
      {/* Photo Circle */}
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={previewUrl || undefined}
          sx={{
            width: { xs: 100, sm: 120 },
            height: { xs: 100, sm: 120 },
            backgroundColor: '#f8f9fa',
            border: '2px dashed #dee2e6',
            '&:hover': {
              backgroundColor: '#e9ecef',
            },
            transition: 'background-color 0.3s ease',
          }}
        >
          {!previewUrl && (
            <Person
              sx={{
                fontSize: { xs: 40, sm: 50 },
                color: '#6c757d',
              }}
            />
          )}
        </Avatar>
        
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '50%',
            }}
          >
            <CircularProgress size={30} sx={{ color: '#007bff' }} />
          </Box>
        )}
      </Box>

      {/* Upload Section */}
      <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
        <Typography
          variant="subtitle1"
          sx={{
            mb: 1,
            fontSize: '14px',
            fontWeight: 500,
            color: '#212529',
          }}
        >
          Your Photo
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<PhotoCamera />}
          onClick={handleUploadClick}
          disabled={loading}
          sx={{
            backgroundColor: isUploaded ? '#28a745' : '#007bff',
            color: '#ffffff',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: isUploaded ? '#218838' : '#0056b3',
            },
            '&:disabled': {
              backgroundColor: '#6c757d',
            },
            transition: 'background-color 0.3s ease',
          }}
        >
          {loading ? 'Uploading...' : isUploaded ? 'Photo Uploaded' : 'Upload Photo'}
        </Button>
        
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            fontSize: '12px',
            color: '#6c757d',
          }}
        >
          JPG, PNG or GIF (max 5MB)
        </Typography>
      </Box>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default PhotoUpload;